import { Tooltip } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Bars } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft } from '@insight-health-ai/roseate/icons';
import { useParams } from 'react-router-dom';
import { getAppointmentByUuid } from 'src/store';
import { Button } from '../../../components/baseComponents';
import 'moment-timezone';
import { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { storeInLocal } from 'src/lib/storage';
import MobileEncounterDetails from 'src/modules/ambient_solo/components/PastEncounter/MobileEncounterDetails';
import PastEncounters from 'src/modules/ambient_solo/components/PastEncounter/PastEncounter';
import {
  createEncounter,
  listEncounters,
} from 'src/modules/ambient_solo/store/actions/encounter';
import { getDictateSummary } from 'src/modules/ambient_solo/store/actions/summary';
import { regenerateIntakeSummary } from 'src/store/actions/appointment.action';
import {
  scheduleLoading,
  getNewIntakeSummary,
  getAppointmentUuidSlice,
} from 'src/store/slices/appointments.slice';
import { startVoiceRecorder } from 'src/store/slices/careConnect.slice';
import { doStream, endStream } from 'src/utilities/revAi';
import { FeedbackForm } from '../../../components/pageComponents/Feedbackform';
import Editor from '../../../components/pageComponents/Editor';
import { DictateMic } from '../../../icons';
import { PatientNotes } from './patientNotes/PatientNotes';

import SchedulePageHeader from './SchedulePageHeader';
import ScheduleEditorButtons from './pageComponents/ScheduleEditorButtons';
import { formatEncounterStatus } from '../../ambient_solo/utils/encounterHelper';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import IntakeSummaryForms from './IntakeSummaryForms';
import { getMagicEditSuggestions } from 'src/modules/ambient_solo/store/actions/summary';
import { analytics } from 'src/services';
import { Rating } from 'src/components';
import { SegmentedTabs } from '../../ambient_solo/components/PastEncounter/SegmentedTabs';
import { usePageHeader } from 'src/hooks/misc';

// TODO: unused setstate functions.
const SchedulePage = () => {
  const dispatch = useDispatch();
  const [textAreaValue, setTextAreaValue] = useState('');
  const [, setIsScheduleLoading] = useState(false);
  const [, setIsIntakeSummaryLoading] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [feedbackScore, setFeedbackScore] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [, setPartialInputText] = useState('');
  const [summaryText, setSummaryText] = useState('');
  const [summaryStatus, setSummaryStatus] = useState('');
  const [, setPhysicalExamOptions] = useState([]);
  const [, setDiagnosticsOptions] = useState([]);
  const [, setAssessmentPlanOptions] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const isMobile = window.innerWidth < 1260;
  const { getUuid, scheduleLoader, newIntakeSummary } = useSelector(
    (state) => state?.appointmentState
  );
  const { isEncounterUpdateLoading } = useSelector(
    (state) => state?.encounterState
  );

  usePageHeader({
    title: 'Patient History',
    breadcrumb: [
      {
        icon: <ChevronLeft />,
        title: 'Back to Appointments',
        href: '/appointments',
      },
    ],
  });

  const {
    isListening,
    revaiResponse,
    revaiPartialResponse,
    physicalExamRevAiResponse,
    physicalExamRevAiPartialResponse,
    diagnosticsRevAiResponse,
    diagnosticsRevAiPartialResponse,
    assessmentRevAiResponse,
    assessmentRevAiPartialResponse,
    physicalExaminationList,
    diagnosticImagingList,
    planList,
  } = useSelector((state) => state?.careConnectState);

  const { magicEditSuggestions } = useSelector((state) => state?.summaryState);

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { isEntitled: isAuraEnabled } = useFeatureEntitlements('ambient');
  const [currentEncounter, setCurrentEncounter] = useState(null);

  const [completionStatus, setCompletionStatus] = useState('');
  const [inVisitTabVisible, setInVisitTabVisible] = useState(false);

  const { selectedEncounter } = useSelector((state) => state?.encounterState);
  const { user } = useAuth();

  const [activeButton, setActiveButton] = useState('');
  const [tabs, setTabs] = useState([]);
  const [autoResumeVisitEnabled, setAutoResumeVisitEnabled] = useState(false);
  const enableVcaPatientHistory =
    currentPractitioner?.org_settings?.enable_vca_patient_history;
  const closeFeedbackModal = () => {
    setFeedbackOpen(false);
  };
  const { id } = useParams();
  storeInLocal(`${currentPractitioner?.org_uuid}_appointmentUUID`, id);
  const quillRef = useRef();
  const physicalRef = useRef();
  const diagnosticRef = useRef();
  const planRef = useRef();

  const isMounted = useRef(true);
  const creatingEncounterOnFirstLoadRef = useRef(false);
  const currentAppointmentIdRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    // Clear any existing appointment data when component mounts
    if (id !== currentAppointmentIdRef.current) {
      currentAppointmentIdRef.current = id;
      // Reset local state to prevent data mixing
      setTextAreaValue('');
      setSummaryText('');
      setSummaryStatus('');
      setCompletionStatus('');
      setActiveButton('');
      setTabs([]);
      setCurrentEncounter(null);
      setAutoResumeVisitEnabled(false);
      setInVisitTabVisible(false);
      setSuggestions({});
    }

    return () => {
      isMounted.current = false;
      // Clean up appointment data when component unmounts
      if (currentAppointmentIdRef.current === id) {
        dispatch(getAppointmentUuidSlice({}));
        dispatch(getNewIntakeSummary({ intakeSummary: null, id: null }));
      }
    };
  }, [id, dispatch]);

  useEffect(() => {
    setIsScheduleLoading(scheduleLoader);
  }, [scheduleLoader]);

  const handleReloadIntakeSummary = () => {
    // Validate that we're working with the correct appointment
    const currentAppointmentId =
      getUuid?.appointment_details?.appt_uuid || getUuid?.appt_uuid;
    if (currentAppointmentId !== id) {
      console.warn('Cannot reload summary: appointment ID mismatch');
      return;
    }

    setIsOverlayVisible(true);
    setIsIntakeSummaryLoading(true);
    analytics.track('Regenerated CareConnect Summary', {
      appointment_date: getUuid?.appointment_details?.created_at,
      appointment_id: getUuid?.appointment_details?.appointment_id,
      appointment_status: getUuid?.appointment_details?.chat_completion_status,
      practitioner_name: `${getUuid?.appointment_details?.practitioner_firstName} ${getUuid?.appointment_details?.practitioner_last_name}`,
      email: user?.email,
    });
    dispatch(
      regenerateIntakeSummary(getUuid?.appointment_details?.appointment_id)
    ).then(() => {
      if (!isMounted.current) return;
      // Ensure we only update the correct record
      dispatch(getAppointmentByUuid(id));
      dispatch(scheduleLoading(false));
      setIsIntakeSummaryLoading(false);
      setIsOverlayVisible(false);
    });
  };

  const intakeStatus = getUuid?.appointment_details
    ? getUuid?.appointment_details?.chat_completion_status
    : getUuid?.chat_completion_status;

  //physicalExamination Dropdown & Textarea values
  useEffect(() => {
    if (
      Array.isArray(physicalExaminationList) &&
      physicalExaminationList.length > 0
    ) {
      const PhysicalExamOptions = physicalExaminationList.map((key) => ({
        value: key?.condition_id,
        label: key?.template_name,
      }));
      setPhysicalExamOptions(PhysicalExamOptions);
    }
  }, [physicalExaminationList]);

  useEffect(() => {
    setSuggestions(magicEditSuggestions);
  }, [magicEditSuggestions]);

  const tooltipSummaryTitle = (
    <span className="">
      Patient has not completed the chat. This is a partial summary.
    </span>
  );

  const isAuraOnly = () => {
    const condition_name = getUuid?.condition_name;
    return condition_name == 'Unknown';
  };

  const isVCACompleted = () => {
    const completion_status =
      getUuid?.appointment_details?.chat_completion_status ||
      getUuid?.chat_completion_status;
    return completion_status === 'Completed';
  };

  const shouldRestrictTemplates = () => {
    if (!isVCACompleted()) {
      return false;
    } else if (isAuraOnly()) {
      return false;
    } else if (
      currentPractitioner?.org_settings?.bypass_aura_combo_template_restrictions
    ) {
      return false;
    }
    return true;
  };
  const getEncounterStatus = useCallback(() => {
    const { displayStatus } = formatEncounterStatus(
      selectedEncounter?.encounter_id ? selectedEncounter : currentEncounter
    );
    return displayStatus;
  }, [currentEncounter, selectedEncounter]);

  const setSelectedTab = useCallback(() => {
    setAutoResumeVisitEnabled(false);

    const encounter_status = getEncounterStatus();
    if (
      encounter_status === 'Completed' ||
      encounter_status === 'Copied to EHR' ||
      encounter_status === 'Saved to EHR'
    ) {
      setActiveButton('In visit');
    } else if (encounter_status.startsWith('Pre-chart')) {
      setActiveButton('Pre-chart');
    } else {
      if (intakeStatus === 'Unavailable') {
        setActiveButton('In visit');
      } else {
        setActiveButton('VCA summary');
      }
    }
  }, [intakeStatus]);

  useEffect(() => {
    dispatch(getAppointmentByUuid(id));
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_startDate`);
    localStorage.removeItem(`${currentPractitioner?.org_uuid}_endDate`);
    analytics.page();
  }, [currentPractitioner?.org_uuid, dispatch, id]);

  const handleFeedbackOpen = (score) => {
    analytics.track('Feedback Voted CareConnect Summary', {
      appointment_date: getUuid?.appointment_details?.created_at,
      appointment_id: getUuid?.appointment_details?.appointment_id,
      appointment_status: getUuid?.appointment_details?.chat_completion_status,
      practitioner_name: `${getUuid?.appointment_details?.practitioner_firstName} ${getUuid?.appointment_details?.practitioner_last_name}`,
      rating: score,
      thumbs: score <= 2 ? 'Down' : 'Up',
      email: user?.email,
    });
    setFeedbackOpen(true);
    setFeedbackScore(score);
  };

  // Start: VCA summary Recording
  useEffect(() => {
    if (revaiResponse) {
      const quill = quillRef.current?.getEditor();
      if (!quill) {
        return;
      }
      const cursorPosition = quill.getSelection()?.index || 0;
      quill.insertText(cursorPosition, revaiResponse, 'user');
      quill.setSelection(
        cursorPosition + revaiResponse.length,
        Quill.sources.SILENT
      );
      quillRef.current.focus(); // Restore focus to the Quill editor
    }
  }, [revaiResponse]);

  useEffect(() => {
    revaiPartialResponse && setPartialInputText(revaiPartialResponse);
  }, [revaiPartialResponse]);

  useEffect(() => {
    getUuid?.appointment_details?.appointment_id
      ? analytics.track('Patient Details Clicked CareConnect Summary', {
          appointment_date: getUuid?.appointment_details?.created_at,
          appointment_id: getUuid?.appointment_details?.appointment_id,
          appointment_status:
            getUuid?.appointment_details?.chat_completion_status,
          is_collapsed: 'False',
          practitioner_name: `${getUuid?.appointment_details?.practitioner_firstName} ${getUuid?.appointment_details?.practitioner_last_name}`,
          email: user?.email,
        })
      : '';
  }, []);

  const handleIntakeSummaryRecording = () => {
    analytics.track('Microphone Clicked CareConnect Summary', {
      appointment_date: getUuid?.appointment_details?.created_at,
      appointment_id: getUuid?.appointment_details?.appointment_id,
      appointment_status: getUuid?.appointment_details?.chat_completion_status,
      practitioner_name: `${getUuid?.appointment_details?.practitioner_firstName} ${getUuid?.appointment_details?.practitioner_last_name}`,
      section: 'VCA summary',
      email: user?.email,
    });
    const type = 'intake_summary';
    const quill = quillRef.current?.getEditor();

    if (isListening) {
      endStream();
      dispatch(startVoiceRecorder(false));
    } else {
      doStream(type);
      dispatch(startVoiceRecorder(true));
      // If the cursor is not pointing anywhere, insert text at the beginning
      if (quill && !quill.getSelection()) {
        quill.insertText(0, '\n', 'user');
        quill.setSelection(0, Quill.sources.SILENT);
        quillRef.current.focus();
      }
    }
  };
  // End: VCA summary Recording

  useEffect(() => {
    const inVisitTabVisible = tabs.find((tab) => tab.label === 'In visit');
    setInVisitTabVisible(!!inVisitTabVisible);
  }, [tabs]);

  const handleStartAuraVisit = () => {
    analytics.track('Clicked Start Aura Visit From VCA Summary Page', {
      appointment_date: getUuid?.appointment_details?.created_at,
      appointment_id: getUuid?.appointment_details?.appointment_id,
      appointment_status: getUuid?.appointment_details?.chat_completion_status,
      practitioner_name: `${getUuid?.appointment_details?.practitioner_firstName} ${getUuid?.appointment_details?.practitioner_last_name}`,
      section: 'Clicked Start Aura Visit',
      email: user?.email,
    });

    if (inVisitTabVisible) {
      setAutoResumeVisitEnabled(true);
      if (isMobile) setActiveButton('In visit');
    }
  };

  // End: VCA summary Recording

  const createEncounterOnFirstLoad = async () => {
    if (creatingEncounterOnFirstLoadRef.current) {
      return;
    }

    creatingEncounterOnFirstLoadRef.current = true;
    const searchFilters = {
      appointment_id: getUuid?.appointment_id,
      from_vca: true,
    };
    try {
      const encounters = await dispatch(listEncounters(searchFilters, false));
      if (!encounters || encounters.length === 0) {
        const current_date = new Date();
        const formattedDate = `${current_date.getMonth() + 1}/${current_date.getDate()}`;
        const patientLastName = getUuid?.patient_details?.last_name;
        const patientFirstName = getUuid?.patient_details?.first_name;
        const encounterPrefix = patientLastName
          ? patientFirstName
            ? `${patientLastName}, ${patientFirstName}`
            : patientLastName
          : patientFirstName || 'VCA Encounter';
        const defaultDescription = `${encounterPrefix} - ${formattedDate}`;
        const data = {
          description: defaultDescription,
          appointment_id: getUuid?.appointment_id,
          practitioner_id: getUuid?.practitioner_id,
          start_time: new Date(),
        };
        await dispatch(
          createEncounter(data, (encounter) => {
            setCurrentEncounter(encounter);
            analytics.track(
              'Automatically Created Encounter From VCA Summary Page',
              {
                practitioner_id: getUuid?.practitioner_id,
                appointment_id: getUuid?.appointment_id,
                encounter_id: encounter.encounter_id,
                email: user?.email,
              }
            );
          })
        );
      } else {
        setCurrentEncounter(encounters[0]);
        await dispatch(getDictateSummary(encounters[0].encounter_id));
      }
    } finally {
      creatingEncounterOnFirstLoadRef.current = false;
    }
  };

  const handleTabClick = (tabValue) => {
    setActiveButton(tabValue);
    setAutoResumeVisitEnabled(false);
  };

  const configureTabs = () => {
    let pageTabs = [
      {
        label: 'VCA summary',
        value: 'VCA summary',
      },
    ];
    if (isAuraEnabled) {
      pageTabs.push(
        {
          label: 'Pre-chart',
          value: 'Pre-chart',
          showBanner:
            currentEncounter?.pre_chart_status === 'clinical-note-generated',
        },
        {
          label:
            getEncounterStatus() === 'Completed' ||
            getEncounterStatus() === 'Copied to EHR' ||
            getEncounterStatus() === 'Saved to EHR'
              ? isMobile
                ? 'Full Note'
                : 'Completed note'
              : 'In visit',
          value: 'In visit',
        },
        {
          label: 'Dictate',
          value: 'Dictate',
        },
        {
          label: isMobile ? 'Documents' : 'Documents',
          value: 'Documents',
        }
      );
    }

    if (intakeStatus === 'Unavailable') {
      pageTabs = pageTabs.slice(1);
    }

    setTabs(pageTabs);
  };
  //assessmentPlan Dropdown & Textarea values
  useEffect(() => {
    if (Array.isArray(planList) && planList.length > 0) {
      const assessmentPlanOptions = planList.map((key) => ({
        value: key?.condition_id,
        label: key?.template_name,
      }));
      setAssessmentPlanOptions(assessmentPlanOptions);
    }
  }, [planList]);

  useEffect(() => {
    if (getUuid?.appointment_id && isAuraEnabled) {
      setCurrentEncounter(null);
      createEncounterOnFirstLoad();
    }
  }, [getUuid?.appointment_id, isAuraEnabled]);

  // Start: Assessment & Plan Recording
  useEffect(() => {
    if (assessmentRevAiResponse) {
      const quill = planRef.current?.getEditor();
      if (!quill) {
        return;
      }
      const cursorPosition = quill.getSelection()?.index || 0;
      quill.insertText(cursorPosition, assessmentRevAiResponse, 'user');
      quill.setSelection(
        cursorPosition + assessmentRevAiResponse.length,
        Quill.sources.SILENT
      );
      planRef.current.focus(); // Restore focus to the Quill editor
    }
  }, [assessmentRevAiResponse]);

  useEffect(() => {
    assessmentRevAiPartialResponse &&
      setPartialInputText(assessmentRevAiPartialResponse);
  }, [assessmentRevAiPartialResponse]);

  //diagnosticImaging Dropdown & Textarea values
  useEffect(() => {
    if (
      Array.isArray(diagnosticImagingList) &&
      diagnosticImagingList.length > 0
    ) {
      const diagnosticsOptions = diagnosticImagingList.map((key) => ({
        value: key?.condition_id,
        label: key?.template_name,
      }));
      setDiagnosticsOptions(diagnosticsOptions);
    }
  }, [diagnosticImagingList]);

  useEffect(() => {
    configureTabs();
  }, [selectedEncounter, currentEncounter]);

  // Start: Physical Examination Recording
  useEffect(() => {
    if (physicalExamRevAiResponse) {
      const quill = physicalRef.current?.getEditor();
      if (!quill) {
        return;
      }
      const cursorPosition = quill.getSelection()?.index || 0;
      quill.insertText(cursorPosition, physicalExamRevAiResponse, 'user');
      quill.setSelection(
        cursorPosition + physicalExamRevAiResponse.length,
        Quill.sources.SILENT
      );
      physicalRef.current.focus(); // Restore focus to the Quill editor
    }
  }, [physicalExamRevAiResponse]);

  useEffect(() => {
    physicalExamRevAiPartialResponse &&
      setPartialInputText(physicalExamRevAiPartialResponse);
  }, [physicalExamRevAiPartialResponse]);

  // Start: Diagnostic&Imaging Recording
  useEffect(() => {
    if (diagnosticsRevAiResponse) {
      const quill = diagnosticRef.current?.getEditor();
      if (!quill) {
        return;
      }
      const cursorPosition = quill.getSelection()?.index || 0;
      quill.insertText(cursorPosition, diagnosticsRevAiResponse, 'user');
      quill.setSelection(
        cursorPosition + diagnosticsRevAiResponse.length,
        Quill.sources.SILENT
      );
      diagnosticRef.current.focus(); // Restore focus to the Quill editor
    }
  }, [diagnosticsRevAiResponse]);

  useEffect(() => {
    diagnosticsRevAiPartialResponse &&
      setPartialInputText(diagnosticsRevAiPartialResponse);
  }, [diagnosticsRevAiPartialResponse]);

  useEffect(() => {
    dispatch(getNewIntakeSummary(null));
    dispatch(getMagicEditSuggestions(null));
  }, [dispatch]);

  useEffect(() => {
    // Validate that the appointment data belongs to the current appointment
    const currentAppointmentId =
      getUuid?.appointment_details?.appt_uuid || getUuid?.appt_uuid;
    const expectedAppointmentId = id;

    if (
      currentAppointmentId &&
      expectedAppointmentId &&
      currentAppointmentId !== expectedAppointmentId
    ) {
      console.warn(
        'Appointment data mismatch detected. Expected:',
        expectedAppointmentId,
        'Got:',
        currentAppointmentId
      );
      return;
    }

    const textAreaValue =
      newIntakeSummary?.id &&
      newIntakeSummary?.id == getUuid?.appointment_details?.appointment_id
        ? newIntakeSummary?.intakeSummary
        : getUuid?.appointment_details?.supervised_intake_summary
          ? getUuid?.appointment_details?.supervised_intake_summary
          : getUuid?.appointment_details?.llm_intake_summary
            ? getUuid?.appointment_details?.llm_intake_summary
            : '';

    const formatText = (text) => {
      let formattedText = (text || '').replace(/\n/g, '<br>');
      formattedText = formattedText.replace(/{[^:]+:\s*'([^']*)'}/g, '$1');
      formattedText = formattedText.replace(/(\d)(?=\s*-\s*no)/g, '$1 ');
      // formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong style="color: #00d091;">$1</strong>');
      // formattedText = formattedText.replace(/<strong style='color: #00d091;'>(.*?)<\/strong>/g, '<strong style="color: #00d091;">$1</strong>');
      return formattedText;
    };

    const formattedTextAreaValue = formatText(textAreaValue);
    setTextAreaValue(formattedTextAreaValue);

    const summaryCreatedOnText = getUuid?.appointment_details
      ?.llm_intake_created
      ? `Created on: ${moment(getUuid?.appointment_details?.llm_intake_created)
          .tz(moment.tz.guess())
          .format('MMM Do, YYYY, h.mm A')}`
      : '';
    setSummaryText(summaryCreatedOnText);
    let summaryStatus = null;
    const chat_completion_status =
      getUuid?.appointment_details?.chat_completion_status ||
      getUuid?.chat_completion_status;

    if (chat_completion_status === 'partial_completed') {
      summaryStatus = 'Partial Summary';
    }
    setSummaryStatus(summaryStatus);

    if (
      getUuid?.appointment_details?.chat_completion_status ||
      getUuid?.chat_completion_status
    ) {
      setCompletionStatus(
        getUuid?.appointment_details?.chat_completion_status ||
          getUuid?.chat_completion_status
      );
      setSelectedTab();
    }
  }, [getUuid, newIntakeSummary, completionStatus, currentEncounter, id]);

  return completionStatus !== '' ? (
    <div className={`page-wrapper care-connect-page`}>
      <SchedulePageHeader getUuid={getUuid} />
      <div className={'lg:flex gap-3 justify-between mt-4 items-center '}>
        <SegmentedTabs
          options={tabs}
          activeTab={activeButton}
          setActiveTab={handleTabClick}
        />
      </div>

      <div
        className={`${
          activeButton == 'VCA summary'
            ? 'rounded-xl bg-white shadow-xl py-3.5 mt-4 mb-8'
            : ''
        }`}
      >
        {activeButton === 'VCA summary' && (
          <>
            {(completionStatus === 'Completed' ||
              completionStatus === 'partial_completed') &&
              activeButton === 'VCA summary' && (
                <div className="flex justify-between items-start flex-col sm:flex-row">
                  <div className="flex items-center gap-3 ml-3">
                    {completionStatus === 'partial_completed' &&
                      activeButton === 'VCA summary' && (
                        <span
                          className={`flex items-center justify-start mb-3.5 flex-wrap ${
                            isMobile ? 'p-2 gap-1' : 'mx-3 gap-3'
                          }`}
                        >
                          <div
                            className="flex"
                            style={{ fontFamily: 'Poppins' }}
                          >
                            <Tooltip
                              title={isMobile ? null : tooltipSummaryTitle}
                              placement="topLeft"
                            >
                              <div className="text-[#ff6f00] flex items-center gap-2 w-max font-normal bg-[#ffe1ca] rounded-full px-4 py-1">
                                <span>{summaryStatus}</span>
                              </div>
                            </Tooltip>
                          </div>
                        </span>
                      )}
                  </div>
                  <div className="flex items-end w-full relative">
                    {inVisitTabVisible && !isMobile && (
                      <div className="flex justify-center items-center gap-3 absolute left-1/2 transform -translate-x-1/2">
                        <Tooltip
                          title={isMobile ? null : 'Start Aura visit'}
                          placement="topLeft"
                        >
                          <Button
                            className={`rounded-xl drop-shadow-sm flex bg-[#00D090] hover:bg-[#059669] p-2 min-w-fit`}
                            onClick={handleStartAuraVisit}
                            disabled={isEncounterUpdateLoading}
                          >
                            <div className="flex items-center mx-1.5 cursor-pointer">
                              {isEncounterUpdateLoading ? (
                                <Bars
                                  height="16"
                                  width="16"
                                  color="#ffffff"
                                  ariaLabel="bars-loading"
                                  wrapperStyle={{ marginRight: '10px' }}
                                />
                              ) : (
                                <DictateMic
                                  className={`cursor-pointer mr-2.5`}
                                />
                              )}
                              {isEncounterUpdateLoading
                                ? 'Starting Aura...'
                                : 'Start Aura visit'}
                            </div>
                          </Button>
                        </Tooltip>
                        {enableVcaPatientHistory && (
                          <PatientNotes
                            schedulepage={true}
                            isEnabled={true}
                            source="vca_patient_history"
                            patientDetails={getUuid?.patient_details}
                          />
                        )}
                      </div>
                    )}
                    <div className="ml-auto">
                      <ScheduleEditorButtons
                        textAreaValue={textAreaValue}
                        handleReloadIntakeSummary={handleReloadIntakeSummary}
                        getUuid={getUuid}
                        isOverlayVisible={isOverlayVisible}
                      />
                    </div>
                  </div>
                </div>
              )}

            {completionStatus === 'Completed' ||
              (completionStatus === 'partial_completed' &&
                activeButton === 'VCA summary' && (
                  <div className="border-b border-gray-200"></div>
                ))}

            <div className="flex justify-center text-xs text-gray-400 my-3">
              {summaryText}
            </div>

            {(completionStatus === 'Completed' ||
              completionStatus === 'partial_completed') &&
              inVisitTabVisible &&
              isMobile && (
                <div className="flex justify-center text-xs text-gray-400 mx-2 mb-3">
                  <Tooltip
                    title={isMobile ? null : 'Start Aura visit'}
                    placement="topLeft"
                  >
                    <>
                      <Button
                        className={`rounded-xl drop-shadow-sm flex bg-[#00D090] hover:bg-[#059669] p-2`}
                        onClick={handleStartAuraVisit}
                        disabled={isEncounterUpdateLoading}
                      >
                        <div className="flex items-center mx-1.5 cursor-pointer">
                          {isEncounterUpdateLoading ? (
                            <Bars
                              height="16"
                              width="16"
                              color="#ffffff"
                              ariaLabel="bars-loading"
                              wrapperStyle={{ marginRight: '10px' }}
                            />
                          ) : (
                            <DictateMic className={`cursor-pointer mr-2.5`} />
                          )}
                          {isEncounterUpdateLoading
                            ? 'Starting Aura...'
                            : 'Start Aura visit'}
                        </div>
                      </Button>
                    </>
                  </Tooltip>
                </div>
              )}
          </>
        )}
        <div>
          {activeButton === 'VCA summary' ? (
            <div>
              <div
                className={`relative overflow-auto schedule_page_wrapper h-full mt-2"}`}
              >
                <div
                  className="editor-container"
                  style={{ overflow: isOverlayVisible ? 'hidden' : 'auto' }}
                >
                  {isOverlayVisible && (
                    <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-20 z-10 flex justify-center items-center">
                      <div className="">
                        <Bars
                          height="25"
                          width="25"
                          color="#00D090"
                          ariaLabel="bars-loading"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <Editor
                      theme="snow"
                      className="quill-editor"
                      value={
                        completionStatus === 'Completed' ||
                        completionStatus === 'partial_completed'
                          ? textAreaValue
                          : 'Summary will be available when patient completes their conversation with lumi'
                      }
                      onChange={(value) => setTextAreaValue(value)}
                      disabled={isOverlayVisible}
                      enableDotPhrases={true}
                    />
                  </div>
                </div>
                {(completionStatus === 'Completed' ||
                  completionStatus === 'partial_completed') && (
                  <div className="sticky bottom-0 flex w-full justify-between">
                    <div className="flex justify-center items-center w-full">
                      <Rating
                        defaultValue={feedbackScore}
                        modalOpen={false}
                        onCompletion={async ({ value }) => {
                          handleFeedbackOpen(value);
                        }}
                      />
                    </div>
                    {/* <div>
                      {isListening ? (
                        <>
                          <PauseIcon
                            fill="#d9534f"
                            visible={true}
                            className={'cursor-pointer'}
                            onClick={handleIntakeSummaryRecording}
                          />
                        </>
                      ) : (
                        <VoiceIcon
                          onClick={handleIntakeSummaryRecording}
                          className={'cursor-pointer'}
                        />
                      )}
                    </div> */}
                  </div>
                )}
              </div>
              <IntakeSummaryForms />
            </div>
          ) : null}
          {isAuraEnabled && (
            <>
              {!isMobile ? (
                <PastEncounters
                  schedulepage={true}
                  activeTab={activeButton}
                  setActiveButton={setActiveButton}
                  patientDetails={getUuid?.patient_details}
                  appointmentId={
                    getUuid?.appointment_details?.appointment_id
                      ? getUuid?.appointment_details?.appointment_id
                      : getUuid?.appointment_id
                  }
                  restrictTemplates={shouldRestrictTemplates()}
                  autoResumeVisit={autoResumeVisitEnabled}
                  onAutoResumeComplete={() => {
                    setAutoResumeVisitEnabled(false);
                  }}
                />
              ) : (
                <div className="mt-4">
                  <MobileEncounterDetails
                    topBarInputs={{}}
                    storedParams={{}}
                    schedulepage={true}
                    encounter_id={currentEncounter?.encounter_id}
                    activeTab={activeButton}
                    setActiveButton={setActiveButton}
                    restrictTemplates={shouldRestrictTemplates()}
                    suggestions={suggestions}
                    autoResumeVisitEnabled={autoResumeVisitEnabled}
                    onAutoResumeComplete={() =>
                      setAutoResumeVisitEnabled(false)
                    }
                    isWrapped={true}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {feedbackOpen && (
        <div className="my-2">
          <FeedbackForm
            isOpen={feedbackOpen}
            setIsOpen={setFeedbackOpen}
            onCancel={closeFeedbackModal}
            feedbackScore={feedbackScore}
          />
        </div>
      )}
    </div>
  ) : (
    <div className="absolute top-0 left-0 w-full h-full  flex justify-center items-center">
      <div className="">
        <Bars height="25" width="25" color="#00D090" ariaLabel="bars-loading" />
      </div>
    </div>
  );
};

export default SchedulePage;
